/** The booking-search namespaces wired to their autocomplete fetchers. */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/bookings';

/** Mirrors Group_Tour_User.BOOKING_STATES labels — the backend `state`
 *  namespace matches these case-insensitively (a raw integer works too).
 *  A tiny fixed enum, so suggestions are filtered locally, no API call. */
/** The one booking-state label the manifest cell action pins to. Named rather
 *  than indexed out of BOOKING_STATE_LABELS so a reorder can't silently change
 *  which state a "manifest" means. */
export const BOOKED_STATE_LABEL = 'Booked';

export const BOOKING_STATE_LABELS = [
  'On hold',
  BOOKED_STATE_LABEL,
  'Cancelled',
  'Cancelled on hold',
  'Pending confirmation',
];

/** Mirrors demo.models.BOOKING_SYSTEMS labels (0-based) — the backend
 *  `booking_system` namespace matches these case-insensitively (raw int works
 *  too). A fixed enum, so suggestions are filtered locally, no API call. */
export const BOOKING_SYSTEM_LABELS = [
  'Unknown', 'Ventrata', 'Bokun', 'Rezdy', 'FareHarbor', 'Curated Planet',
  'Anchor', 'TrekkSoft', 'Spreadsheet', 'Monday.com', 'Intrepid', 'TourCMS',
  'Prioticket',
];

/** Mirrors Group_Tour_User.SURVEY_STATES labels (0-based). */
export const SURVEY_STATE_LABELS = [
  'Not sent', 'Sent', 'Opened', 'Submitted', 'Not needed', 'Emitted',
];

/** Mirrors Group_Tour_User.SOURCES labels — the create/update provenance of a
 *  booking record. Matched by LABEL server-side (or the raw integer), so the
 *  declaration order here is irrelevant; it follows the model's for review
 *  convenience. A fixed enum, so suggestions are filtered locally. */
export const SOURCE_LABELS = [
  'Dashboard', 'Self-registration', 'Trip code', 'XML', 'Integration', 'Tag',
  'Booking reference', 'Waiver', 'Other', 'Catalog', 'Parent booking', 'Console',
];

/** The `phase` namespace's values — the row's `phase` (features/records/time/tripPhase). */
export const TRIP_PHASES = ['active', 'future', 'past', 'undated'];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export function buildBookingNamespaces(): NamespaceConfigMap {
  return {
    tenant: {
      label: 'tenant',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code or name.',
      examples: ['tenant=curatedplanet', 'tenant=~mariott'],
    },
    email: {
      label: 'email',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      help: 'Guest email address (exact or contains).',
      examples: ['email=jane@example.com', 'email=~@gmail.com'],
    },
    phone: {
      label: 'phone',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      help: 'Guest phone number.',
      examples: ['phone=+15551234567', 'phone=~555'],
    },
    user: {
      label: 'user',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      help: 'Guest by email or phone (matches either identifier). Pick "by ID" on a suggestion to match one exact guest.',
      examples: ['user=jane@example.com', 'user=~jane'],
    },
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      help: "Guest first or last name. Quote values with spaces.",
      examples: ['name=~smith', 'name="John Smith"'],
    },
    tour: {
      label: 'tour',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tour'),
      help: 'Tour / product name. Pick "by ID" on a suggestion to match one exact tour.',
      examples: ['tour=~sunset', 'tour="City Walking Tour"'],
    },
    date: {
      label: 'date',
      operators: ['exact', 'range'],
      valueType: 'date',
      dateFormat: 'yyyy-MM-dd',
      help: 'Booking date. Use a range (from..to) to span a period.',
      examples: ['date=2024-06-01', 'date=2024-06-01..2024-06-30'],
    },
    // The trip's own instants (start_ts and the computed end) and where the trip stands
    // now. Every phase is a start_ts range server-side, so `phase` is index-friendly.
    start: {
      label: 'start',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      futureWindows: true,
      help: "The trip's start, UTC. `=` matches a whole day; a range takes date-time bounds, or now-relative ones that move with every refresh.",
      examples: ['start=2026-10-03', 'start="2026-10-01 00:00".."2026-10-08 00:00"', 'start=now..now+2d'],
    },
    end: {
      label: 'end',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      futureWindows: true,
      help: "The trip's computed end, UTC: the start plus the tour's length, or a stay's own check-out. `=` matches a whole day.",
      examples: ['end=2026-10-07', 'end="2026-10-01 00:00".."2026-10-08 00:00"', 'end=now-1d..now'],
    },
    phase: {
      label: 'phase',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(TRIP_PHASES),
      help: 'Where the trip stands now: future (not started), active (started, not ended), past (ended) or undated (no start). A cancelled trip keeps its phase.',
      examples: ['phase=active', 'phase=future'],
    },
    departure: {
      label: 'departure',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag', { category: 'departure' }),
      extraFilterParams: { category: 'departure' },
      help: 'Departure label / time slot (a "departure"-category tag).',
      examples: ['departure=08:00', 'departure="Morning slot"'],
    },
    state: {
      label: 'state',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: (query) =>
        Promise.resolve(
          BOOKING_STATE_LABELS.filter((s) =>
            s.toLowerCase().includes(query.toLowerCase()),
          ).map((s) => ({ value: s, label: s })),
        ),
      help: 'Booking state, by label or raw integer value.',
      examples: ['state=booked', 'state="on hold"'],
    },
    // `gtu` — the booking's own DB primary key. NOT `id`: that namespace is
    // custom_id, the booking's id in the source booking system. Only `iexact`
    // is exposed; the backend also accepts `exact` for the same integer, but
    // the DSL's `=` sigil always re-parses to iexact, and with no suggestion
    // rows there is no `=#` by-ID affordance to reach `exact` through.
    gtu: {
      label: 'gtu',
      operators: ['iexact'],
      valueType: 'integer',
      help: "The booking's own database ID (Group_Tour_User PK) — the ID column.",
      examples: ['gtu=1048576'],
    },
    role: {
      label: 'role',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('role'),
      help: 'Staff role on the booking (guides, coordinators). Ordinary guest bookings have none. Pick "by ID" on a suggestion to match one exact role.',
      examples: ['role=Guide', 'role=~coord'],
    },
    agroup: {
      label: 'agroup',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('access_group'),
      help: 'Access group of the booking\'s user — direct membership, inherited from a staff role, or the tenant guest group. Matches the Access groups column. Names repeat across tenants; pick "by ID" to pin one group.',
      examples: ['agroup=Guides', 'agroup=~guest', 'agroup=#412'],
    },
    // Range folds into the VALUE ('2-4', '9+') rather than the DSL's
    // `..` range operator — matches the Pax column's shortcut buttons.
    pax: {
      label: 'pax',
      operators: ['exact'],
      valueType: 'string',
      help: 'People count: a single value, an inclusive range, or open-ended.',
      examples: ['pax=2', 'pax=2-4', 'pax=9+'],
    },
    tag: {
      label: 'tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      help: 'Booking tag by name (groups same-named variants). Pick "by ID" on a suggestion to match one exact tag row (e.g. "Balance due" vs "Balance Due").',
      examples: ['tag=~mariott', 'tag=dinner', 'tag=#906'],
    },
    category: {
      label: 'category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      help: 'Tag category code (groups tags — e.g. "departure", "checkin"). Pick "by ID" on a suggestion to match one exact category.',
      examples: ['category=departure', 'category=~check'],
    },
    // CP-8095: the same tag lookup against the other sources. One namespace per
    // source rather than a `tag` namespace with a source argument — the widget's
    // node shape has no room for a third component (`scope` is the Orders grid's
    // any/all sigil) and allAny must stay statically known per namespace.
    order_tag: {
      label: 'order_tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      help: 'Tag on the ORDER this booking belongs to (CP-8095), not on the booking itself — use `tag` for that. Pick "by ID" on a suggestion to match one exact tag row.',
      examples: ['order_tag=corporate', 'order_tag=~corp', 'order_tag=#906'],
    },
    order_category: {
      label: 'order_category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      help: 'Tag category of a tag on the ORDER this booking belongs to (CP-8095). Pick "by ID" on a suggestion to match one exact category.',
      examples: ['order_category=market', 'order_category=~mark'],
    },
    user_tag: {
      label: 'user_tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      help: 'Tag on the USER ACCOUNT behind this booking (CP-8095), not on the booking itself — use `tag` for that. Pick "by ID" on a suggestion to match one exact tag row.',
      examples: ['user_tag=part-time', 'user_tag=~part', 'user_tag=#906'],
    },
    user_category: {
      label: 'user_category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      help: 'Tag category of a tag on the USER ACCOUNT behind this booking (CP-8095). Pick "by ID" on a suggestion to match one exact category.',
      examples: ['user_category=market', 'user_category=~mark'],
    },
    tour_tag: {
      label: 'tour_tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      help: 'Tag on the TOUR this booking is on (CP-8095), not on the booking itself — use `tag` for that. Pick "by ID" on a suggestion to match one exact tag row.',
      examples: ['tour_tag=winter', 'tour_tag=~wint', 'tour_tag=#906'],
    },
    tour_category: {
      label: 'tour_category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      help: 'Tag category of a tag on the TOUR this booking is on (CP-8095). Pick "by ID" on a suggestion to match one exact category.',
      examples: ['tour_category=market', 'tour_category=~mark'],
    },
    // id/order/supplier/reseller are opaque, near-unique external-system
    // identifiers — no bounded suggestable set, so (unlike the namespaces
    // above) these have no fetchSuggestions.
    id: {
      label: 'id',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: "Booking's ID in the source booking system: its custom_id, or its second ID there (alt id, e.g. Ventrata's native id).",
      examples: ['id=CID-12345', 'id=~CID-123'],
    },
    order: {
      label: 'order',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Order reference from the booking system.',
      examples: ['order=ORD-100', 'order=~ORD-1'],
    },
    departure_key: {
      label: 'departure_key',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help:
        "The operator's code for the departure INSTANCE. Note `departure` is the "
        + 'time-of-day tag; two departures of one tour can share it and differ only here.',
      examples: ['departure_key=167234TVSF2609282', 'departure_key=~167234TVSF2609'],
    },
    supplier: {
      label: 'supplier',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Supplier reference.',
      examples: ['supplier=SUP-1'],
    },
    reseller: {
      label: 'reseller',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Reseller reference.',
      examples: ['reseller=RES-1'],
    },
    // integration/affiliate are opaque external-system identifiers — no
    // suggestable set, so no fetchSuggestions (same as id/order/supplier).
    integration: {
      label: 'integration',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Integration / booking system the booking came from (codename). Unindexed — pair with a tenant or tour filter.',
      examples: ['integration=bokun', 'integration=ventrata'],
    },
    affiliate: {
      label: 'affiliate',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Affiliate-platform external booking ID (affiliate_ext_id).',
      examples: ['affiliate=AFF-123', 'affiliate=~AFF'],
    },
    booking_system: {
      label: 'booking_system',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(BOOKING_SYSTEM_LABELS),
      help: 'Booking system, by label or raw integer value.',
      examples: ['booking_system=Bokun', 'booking_system=2'],
    },
    survey: {
      label: 'survey',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(SURVEY_STATE_LABELS),
      help: 'Post-trip survey state, by label or raw integer value.',
      examples: ['survey=Submitted', 'survey="Not sent"'],
    },
    // created/updated: exact = a whole calendar day; range bounds are precise
    // date-time values (bare dates still parse and span the whole day).
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the booking record was created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-07-01', 'created="2026-07-01 09:00".."2026-07-08 18:00"'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the booking record was last updated, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['updated=2026-07-08', 'updated="2026-07-01 09:00".."2026-07-08 18:00"'],
    },
    // Provenance of the record itself. Unindexed and low-cardinality (like
    // `integration`) — pair with a tenant/tour or created/updated window.
    create_source: {
      label: 'create_source',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(SOURCE_LABELS),
      help: 'How the booking record was created, by label or raw integer. Unindexed — pair with a tenant/tour or created window.',
      examples: ['create_source=Integration', 'create_source="Self-registration"'],
    },
    update_source: {
      label: 'update_source',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(SOURCE_LABELS),
      help: 'How the booking record was last updated, by label or raw integer. Unindexed — pair with a tenant/tour or updated window.',
      examples: ['update_source=Dashboard', 'update_source=Integration'],
    },
  };
}
